import { Plus, Trash2 } from "lucide-react";
import {
  averageDay,
  DAY_LABEL_MAX,
  DAYS_MAX,
  dayTotals,
  GUIDANCE_MAX,
  hasMacros,
  ITEM_MAX,
  ITEMS_PER_MEAL_MAX,
  kcalOf,
  MACRO_MAX,
  MEAL_SLOT_LABEL,
  MEAL_SLOTS,
  MEALS_PER_DAY_MAX,
  mealTotals,
  QUANTITY_MAX,
  type Macros,
  type MealSlot,
  type PlanBody,
} from "@/modules/meal_plans/surfaces";
import { buttonClass } from "./buttons";
import {
  DayMacroHeader,
  grams,
  kcal,
  MACRO_FILL,
  MACRO_KEYS,
  MACRO_LABEL,
  MACRO_TAG,
  MacroLine,
  MacroSplitBar,
  missingNote,
  type MacroKey,
} from "./MacroSummary";
import { FIELD_INPUT, FIELD_LABEL } from "./ui";

/**
 * The body of a meal plan, as an editable tree: guidance, then Days of Meals
 * of Items (H53 part 4, docs/ux/screens/MealPlanEditorScreen.md). Shared by the
 * plan editor and the template editor, because a template is exactly a body.
 *
 * **Controlled, and keyed.** Every row carries a key minted when it is added,
 * so removing the second of three items moves nobody's half-typed text into
 * the wrong row, which index keys would. The keys never leave the browser:
 * `toPlanBody` strips them, and the server re-parses whatever arrives anyway.
 *
 * **Macros (H54) are typed beside each item and totalled as you type.** The
 * totals come from the same `nutrition.ts` the server uses, so what the editor
 * shows while typing is what the reader shows after saving. Calories are never
 * an input: they are worked out from the grams.
 *
 * The limits are enforced twice on purpose: here as `maxLength` and disabled
 * "Add" buttons, so a person is never allowed to type something that will be
 * refused, and on the server by the value object, which is the rule.
 */

export interface EditorItem {
  key: string;
  text: string;
  quantity: string;
  /** Grams as typed, so a half-typed "4." survives a re-render. */
  protein: string;
  carbs: string;
  fat: string;
}

export interface EditorMeal {
  key: string;
  slot: MealSlot;
  items: EditorItem[];
}

export interface EditorDay {
  key: string;
  label: string;
  meals: EditorMeal[];
}

export interface EditorBody {
  guidance: string;
  days: EditorDay[];
}

let counter = 0;
function key(): string {
  counter += 1;
  return `k${counter}`;
}

function blankItem(): EditorItem {
  return { key: key(), text: "", quantity: "", protein: "", carbs: "", fat: "" };
}

function blankMeal(slot: MealSlot): EditorMeal {
  return { key: key(), slot, items: [blankItem()] };
}

function blankDay(index: number): EditorDay {
  return {
    key: key(),
    label: `Day ${index + 1}`,
    meals: [blankMeal("breakfast"), blankMeal("lunch"), blankMeal("dinner")],
  };
}

function gramsText(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

/** A stored body, ready to edit. An empty plan opens with one day to fill. */
export function toEditorBody(body: PlanBody): EditorBody {
  const days = body.days.map((day) => ({
    key: key(),
    label: day.label,
    meals: day.meals.map((meal) => ({
      key: key(),
      slot: meal.slot,
      items: meal.items.map((item) => ({
        key: key(),
        text: item.text,
        quantity: item.quantity,
        protein: gramsText(item.macros?.protein),
        carbs: gramsText(item.macros?.carbs),
        fat: gramsText(item.macros?.fat),
      })),
    })),
  }));
  return {
    guidance: body.guidance,
    days: days.length > 0 ? days : [blankDay(0)],
  };
}

/**
 * What is posted. Blank rows go too; the server drops them (planBody.ts). The
 * grams travel as typed, and the server decides what they mean, including that
 * an item with none typed has no macros at all.
 */
export function toPlanBody(editor: EditorBody): PlanBody {
  return {
    guidance: editor.guidance,
    days: editor.days.map((day) => ({
      label: day.label,
      meals: day.meals.map((meal) => ({
        slot: meal.slot,
        items: meal.items.map(({ text, quantity, protein, carbs, fat }) => ({
          text,
          quantity,
          macros: { protein, carbs, fat } as unknown as Macros,
        })),
      })),
    })),
  };
}

/** A typed gram figure, or null when blank or not a number yet. */
function typedGrams(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 && n <= MACRO_MAX ? n : null;
}

/** An editor item's macros for the live totals, by the same rule the server applies. */
function liveMacros(item: EditorItem): Macros | undefined {
  const values = {
    protein: typedGrams(item.protein),
    carbs: typedGrams(item.carbs),
    fat: typedGrams(item.fat),
  };
  if (values.protein === null && values.carbs === null && values.fat === null) {
    return undefined;
  }
  return { protein: values.protein ?? 0, carbs: values.carbs ?? 0, fat: values.fat ?? 0 };
}

/** The editor's state as a body, for the live totals. Blank items are skipped. */
function liveBody(editor: EditorBody): PlanBody {
  return {
    guidance: editor.guidance,
    days: editor.days.map((day) => ({
      label: day.label,
      meals: day.meals.map((meal) => ({
        slot: meal.slot,
        items: meal.items
          .filter((item) => item.text.trim() !== "")
          .map((item) => {
            const macros = liveMacros(item);
            return macros
              ? { text: item.text, quantity: item.quantity, macros }
              : { text: item.text, quantity: item.quantity };
          }),
      })),
    })),
  };
}

export function MealPlanBodyEditor({
  value,
  onChange,
  errors,
  disabled = false,
}: {
  value: EditorBody;
  onChange: (next: EditorBody) => void;
  /** Rendered field errors, keyed "guidance" and "days". */
  errors?: Record<string, string>;
  disabled?: boolean;
}) {
  const live = liveBody(value);
  const anyMacros = hasMacros(live);

  const setDay = (dayKey: string, change: (day: EditorDay) => EditorDay) =>
    onChange({
      ...value,
      days: value.days.map((day) => (day.key === dayKey ? change(day) : day)),
    });

  const setMeal = (
    dayKey: string,
    mealKey: string,
    change: (meal: EditorMeal) => EditorMeal,
  ) =>
    setDay(dayKey, (day) => ({
      ...day,
      meals: day.meals.map((meal) => (meal.key === mealKey ? change(meal) : meal)),
    }));

  const setItem = (
    dayKey: string,
    mealKey: string,
    itemKey: string,
    change: Partial<EditorItem>,
  ) =>
    setMeal(dayKey, mealKey, (meal) => ({
      ...meal,
      items: meal.items.map((item) => (item.key === itemKey ? { ...item, ...change } : item)),
    }));

  return (
    <fieldset disabled={disabled} className="space-y-5">
      {anyMacros && <AverageDayBar totals={averageDay(live)} />}

      <label className="block">
        <span className={FIELD_LABEL}>General guidance</span>
        <textarea
          name="guidance"
          rows={3}
          maxLength={GUIDANCE_MAX}
          value={value.guidance}
          onChange={(event) =>
            onChange({ ...value, guidance: event.target.value })
          }
          placeholder="Anything that applies every day: water, timing, what to avoid."
          className={FIELD_INPUT}
        />
        {errors?.guidance && (
          <span className="mt-1 block text-sm text-danger">{errors.guidance}</span>
        )}
      </label>

      {errors?.days && (
        <p role="alert" className="text-sm text-danger">
          {errors.days}
        </p>
      )}

      {value.days.map((day, dayIndex) => (
        <section
          key={day.key}
          aria-label={day.label || `Day ${dayIndex + 1}`}
          className="rounded-xl border border-line bg-surface p-4 shadow-sm"
        >
          <div className="mb-3 flex items-end gap-2">
            <label className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span className={FIELD_LABEL}>Day</span>
                {live.days[dayIndex] && (
                  <DayMacroHeader totals={dayTotals(live.days[dayIndex])} inline />
                )}
              </span>
              <input
                value={day.label}
                maxLength={DAY_LABEL_MAX}
                onChange={(event) =>
                  setDay(day.key, (d) => ({ ...d, label: event.target.value }))
                }
                placeholder={`Day ${dayIndex + 1}`}
                className={FIELD_INPUT}
              />
            </label>
            <button
              type="button"
              aria-label={`Remove ${day.label || `day ${dayIndex + 1}`}`}
              onClick={() =>
                onChange({
                  ...value,
                  days: value.days.filter((d) => d.key !== day.key),
                })
              }
              className={buttonClass("ghost", "shrink-0 px-3")}
            >
              <Trash2 className="size-4" aria-hidden strokeWidth={1.8} />
            </button>
          </div>

          <div className="space-y-4">
            {day.meals.map((meal, mealIndex) => (
              <div key={meal.key} className="rounded-lg border border-line p-3">
                <div className="mb-2 flex items-center gap-2">
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Meal</span>
                    <select
                      value={meal.slot}
                      onChange={(event) =>
                        setMeal(day.key, meal.key, (m) => ({
                          ...m,
                          slot: event.target.value as MealSlot,
                        }))
                      }
                      className={`${FIELD_INPUT} font-semibold text-ink`}
                    >
                      {MEAL_SLOTS.map((slot) => (
                        <option key={slot} value={slot}>
                          {MEAL_SLOT_LABEL[slot]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    aria-label={`Remove ${MEAL_SLOT_LABEL[meal.slot].toLowerCase()}`}
                    onClick={() =>
                      setDay(day.key, (d) => ({
                        ...d,
                        meals: d.meals.filter((m) => m.key !== meal.key),
                      }))
                    }
                    className={buttonClass("ghost", "shrink-0 px-3")}
                  >
                    <Trash2 className="size-4" aria-hidden strokeWidth={1.8} />
                  </button>
                </div>

                <ul className="space-y-3">
                  {meal.items.map((item) => {
                    const macros = liveMacros(item);
                    return (
                      <li key={item.key} className="space-y-1.5">
                        <div className="flex gap-2">
                          <label className="min-w-0 flex-[3]">
                            <span className="sr-only">Item</span>
                            <input
                              value={item.text}
                              maxLength={ITEM_MAX}
                              placeholder="Oats with berries"
                              onChange={(event) =>
                                setItem(day.key, meal.key, item.key, { text: event.target.value })
                              }
                              className={FIELD_INPUT}
                            />
                          </label>
                          <label className="min-w-0 flex-[2]">
                            <span className="sr-only">Quantity</span>
                            <input
                              value={item.quantity}
                              maxLength={QUANTITY_MAX}
                              placeholder="1 cup"
                              onChange={(event) =>
                                setItem(day.key, meal.key, item.key, {
                                  quantity: event.target.value,
                                })
                              }
                              className={FIELD_INPUT}
                            />
                          </label>
                          <button
                            type="button"
                            aria-label="Remove item"
                            onClick={() =>
                              setMeal(day.key, meal.key, (m) => ({
                                ...m,
                                items: m.items.filter((i) => i.key !== item.key),
                              }))
                            }
                            className={buttonClass("ghost", "shrink-0 px-3")}
                          >
                            <Trash2 className="size-4" aria-hidden strokeWidth={1.8} />
                          </button>
                        </div>
                        <MacroInputs
                          item={item}
                          kcalValue={macros ? kcalOf(macros) : null}
                          onChange={(change) => setItem(day.key, meal.key, item.key, change)}
                        />
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    disabled={meal.items.length >= ITEMS_PER_MEAL_MAX}
                    onClick={() =>
                      setMeal(day.key, meal.key, (m) => ({
                        ...m,
                        items: [...m.items, blankItem()],
                      }))
                    }
                    className={buttonClass("ghost", "text-sm")}
                  >
                    <Plus className="size-4" aria-hidden strokeWidth={1.8} />
                    Add item
                  </button>
                  {live.days[dayIndex]?.meals[mealIndex] && (
                    <MacroLine totals={mealTotals(live.days[dayIndex].meals[mealIndex])} />
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={day.meals.length >= MEALS_PER_DAY_MAX}
            onClick={() =>
              setDay(day.key, (d) => ({
                ...d,
                meals: [...d.meals, blankMeal("snack")],
              }))
            }
            className={buttonClass("ghost", "mt-3 text-sm")}
          >
            <Plus className="size-4" aria-hidden strokeWidth={1.8} />
            Add meal
          </button>
        </section>
      ))}

      <button
        type="button"
        disabled={value.days.length >= DAYS_MAX}
        onClick={() =>
          onChange({ ...value, days: [...value.days, blankDay(value.days.length)] })
        }
        className={buttonClass("secondary", "w-full")}
      >
        <Plus className="size-4" aria-hidden strokeWidth={1.8} />
        {value.days.length >= DAYS_MAX ? `A plan holds ${DAYS_MAX} days` : "Add day"}
      </button>
    </fieldset>
  );
}

/**
 * Three small gram fields under an item, and the calories they make.
 *
 * **Quiet until used.** Most items in a plan may never get figures, so the
 * fields are compact and placeholder-led rather than a second full-width row of
 * inputs; the calories appear only once there is something to add up.
 */
function MacroInputs({
  item,
  kcalValue,
  onChange,
}: {
  item: EditorItem;
  kcalValue: number | null;
  onChange: (change: Partial<EditorItem>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 pl-1">
      {MACRO_KEYS.map((key: MacroKey) => (
        <label
          key={key}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-page px-2 py-1 focus-within:border-accent"
        >
          <span className={`size-2 rounded-[2px] ${MACRO_FILL[key]}`} aria-hidden="true" />
          <span className="text-xs font-semibold text-muted" aria-hidden="true">
            {MACRO_TAG[key]}
          </span>
          <span className="sr-only">{MACRO_LABEL[key]} in grams</span>
          <input
            inputMode="decimal"
            value={item[key]}
            placeholder="0"
            maxLength={6}
            onChange={(event) =>
              onChange({ [key]: event.target.value.replace(",", ".") } as Partial<EditorItem>)
            }
            className="w-12 bg-transparent text-right text-sm tabular-nums text-ink outline-none placeholder:text-muted/60"
          />
          <span className="text-xs text-muted" aria-hidden="true">
            g
          </span>
        </label>
      ))}
      {kcalValue !== null && (
        <span className="text-xs font-semibold tabular-nums text-body">{kcal(kcalValue)}</span>
      )}
    </div>
  );
}

/**
 * The plan's average day, kept in view while editing (H54): a compact sticky
 * bar rather than the reader's full card, because here it is a gauge to glance
 * at between rows, not a summary to read.
 */
function AverageDayBar({ totals }: { totals: ReturnType<typeof averageDay> }) {
  const note = missingNote(totals.missing);
  return (
    <div className="sticky top-20 z-10 rounded-xl border border-line bg-surface/95 p-3 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Average day</p>
        <p className="text-sm text-body">
          <span className="font-display text-lg font-bold text-ink">{kcal(totals.kcal)}</span>
          {MACRO_KEYS.map((key) => (
            <span key={key} className="ml-3 tabular-nums">
              <span className="sr-only">{MACRO_LABEL[key]} </span>
              <span aria-hidden="true" className="text-muted">
                {MACRO_TAG[key]}{" "}
              </span>
              {grams(totals[key])}
            </span>
          ))}
        </p>
      </div>
      <MacroSplitBar totals={totals} className="mt-2 h-1.5 w-full" />
      {note && <p className="mt-1.5 text-xs text-muted">{note}</p>}
    </div>
  );
}
